// frontend/src/components/EstornoModal/EstornoModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, DatePicker, Button, Alert, Row, Col, InputNumber, message } from 'antd';
import { createEstorno, getFaturasDisponiveis } from '../../services/cardService';
import { updateTransacao } from '../../services/transactionService';
import dayjs from 'dayjs';
import { getErrorMessage } from '../../utils/errorHandler';
import { getMobileFullscreenModalProps } from '../../utils/mobileModal';

const { Option } = Select;

// ADICIONADO: isMobile prop
const EstornoModal = ({ isOpen, onClose, onSave, cartaoId, faturaSugeridaId, transacaoToEdit, isMobile = false }) => {
    const [form] = Form.useForm();
    const [error, setError] = useState('');
    const [faturasDisponiveisState, setFaturasDisponiveisState] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    const isEditing = !!transacaoToEdit;

    useEffect(() => {
        if (isOpen) {
            const fetchFaturas = async () => {
                setIsLoading(true);
                setError('');
                form.resetFields();

                try {
                    const fatData = await getFaturasDisponiveis(cartaoId, faturaSugeridaId);
                    setFaturasDisponiveisState(fatData);

                    if (isEditing) {
                        form.setFieldsValue({
                            ...transacaoToEdit,
                            data: transacaoToEdit.data ? dayjs(transacaoToEdit.data) : null,
                            fatura_id: transacaoToEdit.fatura,
                        });
                    } else {
                        form.setFieldsValue({
                            data: dayjs(),
                            fatura_id: faturaSugeridaId || (fatData.length > 0 ? fatData.find(f => f.status === 'ABERTA')?.id || fatData[0].id : undefined),
                        });
                    }
                } catch (err) {
                    setError(getErrorMessage(err, "Falha ao carregar faturas disponíveis."));
                } finally {
                    setIsLoading(false);
                }
            };
            fetchFaturas();
        }
    }, [isOpen, cartaoId, faturaSugeridaId, transacaoToEdit, isEditing, form]);

    const onFinish = async (values) => {
        setError('');
        setIsLoading(true);
        
        const payload = {
            descricao: values.descricao,
            valor: values.valor,
            data_vencimento: values.data ? dayjs(values.data).format('YYYY-MM-DD') : null,
            fatura_id: values.fatura_id,
            cartao_credito: cartaoId, 
            tipo: 'receita',
        };

        try {
            if (isEditing) {
                await updateTransacao(transacaoToEdit.id, payload);
            } else {
                await createEstorno(payload);
            }
            onSave(true, 'Estorno salvo com sucesso!');
        } catch (err) {
            const errorMessage = getErrorMessage(err, 'Ocorreu um erro ao salvar o estorno.');
            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const handleCancel = () => { !isLoading && onClose(); };

    // Estilos responsivos
    const inputSize = isMobile ? 'large' : 'middle';
    const modalWidth = isMobile ? '100%' : 520;

    const renderFooter = () => {
        if (isMobile) {
            return (
                <Row gutter={[8, 8]}>
                    <Col span={12}>
                        <Button block size="large" onClick={handleCancel} disabled={isLoading}>Cancelar</Button>
                    </Col>
                    <Col span={12}>
                        <Button block size="large" type="primary" loading={isLoading} onClick={() => form.submit()}>Salvar</Button>
                    </Col>
                </Row>
            );
        }
        return [
            <Button key="back" onClick={handleCancel} disabled={isLoading}>Cancelar</Button>,
            <Button key="submit" type="primary" loading={isLoading} onClick={() => form.submit()}>Salvar</Button>,
        ];
    };

    return (
        <Modal
            open={isOpen}
            title={isEditing ? 'Editar Estorno' : 'Lançar Estorno'}
            onCancel={handleCancel}
            destroyOnClose
            width={modalWidth}
            {...getMobileFullscreenModalProps(isMobile)}
            footer={renderFooter()}
        >
            <Form form={form} layout="vertical" onFinish={onFinish} preserve={false} size={inputSize}>
                {error && <Alert message={error} type="error" showIcon style={{ marginBottom: 24 }} />}
                
                <Form.Item name="descricao" label="Descrição" rules={[{ required: true, message: 'Descrição é obrigatória' }]}>
                    <Input />
                </Form.Item>

                <Row gutter={16}>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="valor" label="Valor" rules={[{ required: true, message: 'Valor é obrigatório' }]}>
                            <InputNumber
                                prefix="R$ "
                                style={{ width: '100%' }}
                                precision={2}
                                step={0.01}
                                inputMode="decimal" // Teclado numérico
                                formatter={(value) => {
                                    if (value === null || value === undefined || value === '') return '';
                                    return new Intl.NumberFormat('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(value);
                                }}
                                parser={(value) => {
                                    if (!value) return null;
                                    const onlyNumbers = value.replace(/[^\d]/g, '');
                                    if (!onlyNumbers) return null;
                                    const num = parseInt(onlyNumbers, 10);
                                    return num / 100;
                                }}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={isMobile ? 24 : 12}>
                        <Form.Item name="data" label="Data" rules={[{ required: true, message: 'Data é obrigatória' }]}>
                            <DatePicker format="DD/MM/YYYY" style={{ width: '100%' }} />
                        </Form.Item>
                    </Col>
                </Row>

                <Form.Item name="fatura_id" label="Lançar na Fatura" rules={[{ required: true, message: 'Fatura é obrigatória' }]}>
                    <Select placeholder="Selecione uma fatura..." loading={isLoading}>
                        {faturasDisponiveisState.map(f => <Option key={f.id} value={f.id}>{f.nome}</Option>)}
                    </Select>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default EstornoModal;